/**
 * Pure arc-geometry helpers for the side-selection donut
 * (`components/Fight/SideSelectionDonut.vue`). No Vue, no DOM — just angles
 * and SVG path strings, so the geometry itself is testable without mounting
 * anything.
 *
 * `power_share` arrives pre-divided by the server and should already sum to
 * 1.0 across `sides`, per `05-ui-contract.md` §FightStateDTO additions — the
 * donut does no arithmetic on it beyond turning fractions into arcs. The one
 * exception is the degenerate case where every side reads zero power (a
 * fight whose sides are freshly created and still empty): there the total is
 * zero, so shares fall back to an equal split rather than collapsing every
 * arc to nothing.
 */

const TAU = Math.PI * 2;
const DEFAULT_START_ANGLE = -Math.PI / 2; // 12 o'clock
const MIN_SWEEP = 0.0001;

/**
 * One `{id, share}` per side. Shares always sum to 1 (barring floating-point
 * dust), whether they come straight from `power_share` or, in the all-zero
 * case, from an equal split.
 */
export function computeSideShares(sides) {
  const list = sides || [];
  const total = list.reduce((sum, side) => sum + Math.max(0, side.power_share || 0), 0);
  if (list.length === 0) {
    return [];
  }
  if (total <= 0) {
    const equal = 1 / list.length;
    return list.map(side => ({id: side.id, share: equal}));
  }
  return list.map(side => ({id: side.id, share: Math.max(0, side.power_share || 0) / total}));
}

function polarToCartesian(cx, cy, radius, angle) {
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
}

/**
 * SVG path `d` for one donut segment (an annulus wedge between `innerRadius`
 * and `outerRadius`). Sweep is clamped just under a full turn — a literal
 * 2π arc has identical start and end points, which most SVG renderers draw
 * as nothing at all, so a lone side would otherwise vanish instead of
 * filling the ring.
 */
export function describeDonutArc(cx, cy, innerRadius, outerRadius, startAngle, endAngle) {
  const sweep = Math.min(Math.max(endAngle - startAngle, 0), TAU - MIN_SWEEP);
  const clampedEnd = startAngle + Math.max(sweep, MIN_SWEEP);
  const largeArc = clampedEnd - startAngle > Math.PI ? 1 : 0;

  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, clampedEnd);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, clampedEnd);
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

/**
 * One arc descriptor per side: its angular span, its SVG path, and the point
 * a label would sit at (mid-radius, mid-angle). `gap` is a small angular
 * notch cut from the end of every wedge so adjacent arcs read as separate
 * selectable shapes rather than one solid ring; it never changes a side's
 * `share`, only how much of its sweep is actually drawn.
 */
export function buildSideArcs(sides, options = {}) {
  const {
    cx = 50,
    cy = 50,
    innerRadius = 30,
    outerRadius = 48,
    startAngle = DEFAULT_START_ANGLE,
    gap = 0.02,
    /* How much of the turn the sides get to divide. A full turn unless some of
       it is spoken for — the establishment screen reserves an arc at twelve
       o'clock for the fight itself, and the sides share what is left. */
    totalSweep = TAU,
  } = options;

  const list = sides || [];
  const shares = computeSideShares(list);
  let angle = startAngle;

  return list.map((side, index) => {
    const {share} = shares[index];
    const sweep = share * totalSweep;
    const arcStart = angle;
    const arcEnd = angle + Math.max(sweep - gap, 0);
    angle += sweep;
    const midAngle = (arcStart + arcEnd) / 2;
    const labelRadius = (innerRadius + outerRadius) / 2;
    const label = polarToCartesian(cx, cy, labelRadius, midAngle);

    return {
      id: side.id,
      side,
      share,
      startAngle: arcStart,
      endAngle: arcEnd,
      midAngle,
      path: describeDonutArc(cx, cy, innerRadius, outerRadius, arcStart, arcEnd),
      labelX: label.x,
      labelY: label.y,
    };
  });
}

const DEG = 180 / Math.PI;

/** Screen degrees: 0 points right, 90 points down — CSS `rotate()`'s own frame. */
function toScreenDegrees(angle) {
  return angle * DEG;
}

/** 0-360, with 0 at twelve o'clock — CSS `conic-gradient()`'s frame. */
function toConicDegrees(angle) {
  return ((angle * DEG + 90) % 360 + 360) % 360;
}

/**
 * Which edge of the screen a sector runs out to. Drives where its text block
 * hangs and which way it reads, so a side at nine o'clock is not laid out as
 * if it stood at twelve.
 */
export function sectorZone(midAngle) {
  const deg = ((midAngle * DEG) % 360 + 360) % 360;
  if (deg >= 300 || deg < 60) return 'right';
  if (deg < 120) return 'bottom';
  if (deg < 240) return 'left';
  return 'top';
}

/**
 * A `clip-path` polygon for one sector: the wedge from the centre of the
 * screen out past its corners, bounded by the same two rays that bound the
 * side's donut segment. The far edge is sampled at a radius deliberately
 * larger than the box (150%), so the wedge overshoots every corner instead of
 * rounding off short of it — points outside the box simply clip.
 */
export function sectorClipPath(startAngle, endAngle, radius = 150) {
  const sweep = Math.max(endAngle - startAngle, 0);
  const steps = Math.max(2, Math.ceil(sweep / (Math.PI / 8)));
  const points = ['50% 50%'];

  for (let i = 0; i <= steps; i += 1) {
    const angle = startAngle + (sweep * i) / steps;
    const x = 50 + radius * Math.cos(angle);
    const y = 50 + radius * Math.sin(angle);
    points.push(`${x.toFixed(2)}% ${y.toFixed(2)}%`);
  }

  return `polygon(${points.join(', ')})`;
}

/**
 * The donut's segments, continued outward until they divide the whole screen.
 *
 * The split is EVEN, one equal segment per side, and that is deliberate. A
 * screen sector is not a bar chart: sizing it by `power_share` gives a weak
 * side a slice too narrow to stand a character in, while the strongest side
 * gets half the room and the composition falls apart the moment the numbers
 * are lopsided. Every side is a party to the fight, so every side gets an
 * equal quarter of the screen to be introduced in.
 *
 * Power is still drawn — inside the segment, as `powerPath`: a fill sweeping
 * from the segment's own start across the fraction of it the side's power
 * commands, measured against the strongest side. The strongest wedge fills
 * completely; a side at half its power fills half of its own wedge. The
 * reading is comparative and immediate, and it costs the layout nothing.
 *
 * What this adds beyond the arcs is everything the DOM needs to draw the
 * split without doing trigonometry in a template:
 *
 *   - `rayDeg`    — the separator between this sector and the one before it,
 *                   as a CSS rotation off the centre point.
 *   - `conicFrom` / `conicTo` — the same span in `conic-gradient()`'s frame,
 *                   for the wash that tints the sector's slice of the scene.
 *   - `anchor`    — where this sector's text block sits, as viewport percent.
 *   - `figure`    — where its character stands, further out toward the edge.
 *   - `zone`      — which edge it runs to.
 *
 * Positions are elliptical rather than circular so the layout keeps its
 * proportions on a wide screen: the horizontal reach is a percentage of the
 * width and the vertical of the height, and both are already in the units a
 * `left`/`top` binding wants.
 */
export function buildSideSectors(sides, options = {}) {
  const {
    /* Clear of the ring at its new size: the selector is large now, and a
       text block anchored inside its reach printed over the band. */
    anchorRadiusX = 33,
    anchorRadiusY = 37,
    axialOffsetX = 19,
    figureRadiusX = 47,
    figureRadiusY = 45,
    /* A thin ring, not a pie: the band is the control surface and the middle
       belongs to the fight's state. The clickable area is widened in CSS
       rather than by fattening the band. */
    innerRadius = 39,
    outerRadius = 48,
    cx = 50,
    cy = 50,
    totalSweep = TAU,
    ...arcOptions
  } = options;

  const list = sides || [];
  /* An even split is an even split whatever the powers are, so the arcs are
     built from a uniform weight rather than from `power_share`. */
  const even = list.map(side => ({...side, power_share: 1}));
  const powerShares = computeSideShares(list);
  const strongest = powerShares.reduce((max, entry) => Math.max(max, entry.share), 0);

  return buildSideArcs(even, {...arcOptions, cx, cy, innerRadius, outerRadius, totalSweep}).map((arc, index) => {
    const conicFrom = toConicDegrees(arc.startAngle);
    const zone = sectorZone(arc.midAngle);
    const cos = Math.cos(arc.midAngle);
    const sin = Math.sin(arc.midAngle);
    /* The drawn arc stops short of its true end by `gap`, so the ring reads as
       separate segments. The sector on screen must not: it is bounded by where
       the next side begins, not by the notch between them. */
    const spanEnd = arc.startAngle + arc.share * totalSweep;
    const powerShare = powerShares[index]?.share ?? 0;
    const powerFill = strongest > 0 ? powerShare / strongest : 0;

    return {
      ...arc,
      /* `arc.side` is the uniform stand-in the even split was built from, and
         its `power_share` is a 1 that means "one equal wedge", not this side's
         power. Everything downstream reads the real row. */
      side: list[index],
      zone,
      spanEnd,
      powerShare,
      powerFill,
      powerPath: describeDonutArc(
          cx,
          cy,
          innerRadius,
          outerRadius,
          arc.startAngle,
          arc.startAngle + (arc.endAngle - arc.startAngle) * powerFill,
      ),
      clipPath: sectorClipPath(arc.startAngle, spanEnd),
      /* A thin band is a hard thing to hit. The pointer target is a fat wedge
         spanning the same angles, so the control is as large as the segment
         looks like it should be without the band being drawn any thicker. */
      hitPath: describeDonutArc(
          cx,
          cy,
          Math.max(innerRadius - 12, 0),
          outerRadius + 4,
          arc.startAngle,
          arc.endAngle,
      ),
      midDeg: toScreenDegrees(arc.midAngle),
      rayDeg: toScreenDegrees(arc.startAngle),
      /* This sector's own two boundaries, so the separators either side of the
         side being looked at can be lit while the rest stay near-invisible. */
      edgeStartDeg: toScreenDegrees(arc.startAngle),
      edgeEndDeg: toScreenDegrees(spanEnd),
      /* Unit vector along the segment's bearing. The segment lifts outward
         from the ring on focus, and its sector, figure and text follow it, so
         the whole wedge reads as one object rather than a control plus panels. */
      outX: cos,
      outY: sin,
      conicFrom,
      conicTo: conicFrom + arc.share * (totalSweep / TAU) * 360,
      /* A sector pointing straight up or straight down has its text on the
         ring's own vertical axis, where the selector already is. Those two
         step sideways to stand beside their character instead of on top of
         the control. */
      anchorX: (zone === 'top' || zone === 'bottom') && axialOffsetX
          ? 50 + (cos >= 0 ? 1 : -1) * axialOffsetX
          : 50 + anchorRadiusX * cos,
      anchorY: 50 + anchorRadiusY * sin,
      figureX: 50 + figureRadiusX * cos,
      figureY: 50 + figureRadiusY * sin,
    };
  });
}

/*
 * The arc at twelve o'clock that is NOT a side.
 *
 * The establishment screen is six areas, not five: the fight's own — where it
 * says what it is and where it is being fought — and one per side. Reserving
 * it in the geometry rather than in the stylesheet is what makes the reservation
 * true: the sides divide what is left of the turn, so no side is ever laid out
 * under the title, and the two rays bounding the fight's area are real sector
 * boundaries that the header sits between.
 *
 * A quarter turn, because that is what the title needs. At the screen centre a
 * ±45° wedge reaches the top edge at a quarter and three quarters of the width,
 * which is the room a location name is set in.
 */
export const ARENA_SWEEP = Math.PI / 2;

/**
 * The whole establishment screen as one description: the fight's reserved wedge
 * and one sector per side, sharing the rest of the turn evenly.
 */
export function buildStageLayout(sides, options = {}) {
  const {
    arenaSweep = ARENA_SWEEP,
    cx = 50,
    cy = 50,
    /* A band, not a hairline. The ring is the whole control and the emblem of
       every side is drawn inside it, so it is given the depth to carry one;
       what it must not do is close over the middle, which belongs to the
       clock. */
    innerRadius = 30,
    outerRadius = 47,
    /* Wide enough that six areas read as six from across the room. */
    gap = 0.045,
    /* Where a side's text hangs. One ellipse for every bearing — no sector is
       axial any more now that the top is spoken for, so nothing has to step
       aside from the ring. */
    anchorRadiusX = 32,
    anchorRadiusY = 32,
    ...rest
  } = options;

  const startAngle = DEFAULT_START_ANGLE + arenaSweep / 2;
  const arenaStart = DEFAULT_START_ANGLE - arenaSweep / 2;
  const arenaEnd = DEFAULT_START_ANGLE + arenaSweep / 2;
  const glyph = polarToCartesian(cx, cy, (innerRadius + outerRadius) / 2, DEFAULT_START_ANGLE);

  return {
    arena: {
      startAngle: arenaStart,
      endAngle: arenaEnd,
      path: describeDonutArc(cx, cy, innerRadius, outerRadius, arenaStart + gap / 2, arenaEnd - gap / 2),
      clipPath: sectorClipPath(arenaStart, arenaEnd),
      edgeStartDeg: toScreenDegrees(arenaStart),
      edgeEndDeg: toScreenDegrees(arenaEnd),
      glyphX: glyph.x,
      glyphY: glyph.y,
    },
    /*
     * Laid out right to left, so slot 1 stands on the LEFT.
     *
     * The slots are not interchangeable: `apps/fight/constants.py` opens every
     * fight with slot 1 as the attacker's side and slot 2 as the defender's.
     * Sweeping clockwise from the fight's own wedge put the attacker on the
     * right and the defender on the left, which is the fight read backwards.
     * The list is reversed into the sweep and the sectors reversed back out, so
     * the geometry runs the other way while the caller still gets its sides in
     * slot order.
     */
    sectors: buildSideSectors([...(sides || [])].reverse(), {
      ...rest,
      cx,
      cy,
      innerRadius,
      outerRadius,
      gap,
      anchorRadiusX,
      anchorRadiusY,
      axialOffsetX: 0,
      startAngle,
      totalSweep: TAU - arenaSweep,
    }).reverse(),
  };
}
